// @ts-check
/**
 * ConversationDayRawView.js (Factory Pattern)
 *
 * Specialized raw view for ChatMessaging-ConversationDay.
 * Displays all messages from a single matched day of a conversation. Messages
 * are rendered inline (the chat message shape differs from email events, so it
 * does not reuse CommunicationEventItem).
 */

/** @typedef {import('../../../../stores/referenceUnitStore').ReferenceUnitHandle} ReferenceUnitHandle */

/**
 * @param {any} deps - Injected Vue/store dependencies, see componentRegistry.ts.
 *   Typed as `any` to preserve the factory-pattern escape hatch for
 *   dynamically-loaded configurable types.
 */
export default function createComponent(deps) {
    const {
        ref,
        watch,
        onMounted,
        useReferenceUnitStore,
        useMatterStore,
        formatDate,
    } = deps;

    return {
        name: 'ChatConversationDayRawView',

        props: {
            reference: {
                type: Object,
                required: true,
            },
        },

        /** @param {{ reference: ReferenceUnitHandle }} props */
        setup(props) {
            const referenceUnitStore = useReferenceUnitStore();
            const matterStore = useMatterStore();

            const loading = ref(false);
            const error = ref(null);
            const rawViewModel = ref(null);

            async function fetchRawViewModel() {
                if (!props.reference) {
                    console.warn('[ChatConversationDayRawView] No reference provided');
                    return;
                }

                // Serve a cached raw-view model synchronously (re-selecting or
                // navigating back to a reference is then instant, no refetch).
                const matterId = matterStore.currentMatter?.RecordId;
                const cached = matterId
                    ? referenceUnitStore.getCachedRawViewModel(props.reference, matterId)
                    : null;
                if (cached) {
                    rawViewModel.value = cached;
                    error.value = null;
                    loading.value = false;
                    return;
                }

                loading.value = true;
                error.value = null;

                try {
                    rawViewModel.value = await referenceUnitStore.fetchRawViewModel(
                        props.reference,
                        matterStore.currentMatter.RecordId,
                    );
                } catch (err) {
                    console.error('[ChatConversationDayRawView] Error fetching raw view:', err);
                    error.value = err instanceof Error ? err.message : String(err);
                } finally {
                    loading.value = false;
                }
            }

            onMounted(() => {
                fetchRawViewModel();
            });

            watch(
                () => props.reference,
                () => {
                    fetchRawViewModel();
                },
            );

            /** @param {any} message */
            const senderName = (message) => {
                const s = message.sender;
                if (!s) return 'Unknown';
                return s.natural_identifier || s.primary_email_address || s.primary_phone_number || 'Unknown';
            };

            /** @param {number} messageId */
            const getMessageRecipients = (messageId) => {
                if (!rawViewModel.value || !rawViewModel.value.recipients_by_message) return [];
                return rawViewModel.value.recipients_by_message[messageId] || [];
            };

            return {
                loading,
                error,
                rawViewModel,
                formatDate,
                senderName,
                getMessageRecipients,
            };
        },

        template: `
            <div class="communication-raw-view-content">
                <!-- Loading state -->
                <div v-if="loading" class="loading-state">
                    <div class="loading-dots"><span></span><span></span><span></span></div>
                </div>

                <!-- Error state -->
                <div v-else-if="error" class="error-state">
                    <p>Error loading conversation day: {{ error }}</p>
                </div>

                <!-- Chat day -->
                <div v-else-if="rawViewModel" class="email-thread">
                    <div
                        v-for="message in rawViewModel.messages"
                        :key="message.message_id"
                        class="chat-message-item"
                    >
                        <div class="chat-message-header">
                            <span class="chat-message-sender">{{ senderName(message) }}</span>
                            <span class="chat-message-time">{{ formatDate(message.timestamp) }}</span>
                        </div>
                        <div class="chat-message-recipients" v-if="getMessageRecipients(message.message_id).length">
                            To:
                            <span v-for="(r, i) in getMessageRecipients(message.message_id)" :key="r.entity_id">
                                {{ r.name || r.email }}<span v-if="i < getMessageRecipients(message.message_id).length - 1">, </span>
                            </span>
                        </div>
                        <div class="chat-message-text">{{ message.message_text }}</div>
                    </div>

                    <!-- Empty state -->
                    <div v-if="rawViewModel.messages.length === 0" class="empty-state">
                        No messages found for this day.
                    </div>
                </div>
            </div>
        `,
    };
}
